import types from '../../mutation-types';
import ConversationApi from '../../../api/inbox/conversation';
import MessageApi from '../../../api/inbox/message';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import { createPendingMessage } from 'dashboard/helper/commons';
import {
  buildConversationList,
  isOnMentionsView,
  isOnParticipatingView,
  isOnUnattendedView,
  isOnFoldersView,
} from './helpers/actionHelpers';
import messageReadActions from './actions/messageReadActions';
import messageTranslateActions from './actions/messageTranslateActions';
import * as Sentry from '@sentry/vue';
import {
  handleVoiceCallCreated,
  handleVoiceCallUpdated,
  syncConversationCallVisibility,
} from 'dashboard/helper/voice';

export const hasMessageFailedWithExternalError = pendingMessage => {
  // This helper is used to check if the message has failed with an external error.
  // We have two cases
  // 1. Messages that fail from the UI itself (due to large attachments or a failed network):
  //    In this case, the message will have a status of failed but no external error. So we need to create that message again
  // 2. Messages sent from Chatwoot but failed to deliver to the customer for some reason (user blocking or client system down):
  //    In this case, the message will have a status of failed and an external error. So we need to retry that message
  const { content_attributes: contentAttributes, status } = pendingMessage;
  const externalError = contentAttributes?.external_error ?? '';
  return status === MESSAGE_STATUS.FAILED && externalError !== '';
};

// actions
const actions = {
  getConversation: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.show(conversationId);
      commit(types.UPDATE_CONVERSATION, response.data);
      commit(`contacts/${types.SET_CONTACT_ITEM}`, response.data.meta.sender);
    } catch (error) {
      // Ignore error
    }
  },

  fetchAllConversations: async ({ commit, state, dispatch }) => {
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const params = state.conversationFilters;
      const {
        data: { data },
      } = await ConversationApi.get(params);
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        params.assigneeType
      );
    } catch (error) {
      // Handle error
    }
  },

  fetchFilteredConversations: async ({ commit, dispatch }, params) => {
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const { data } = await ConversationApi.filter(params);
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        'appliedFilters'
      );
    } catch (error) {
      commit(types.CLEAR_LIST_LOADING_STATUS);
      throw error;
    }
  },

  emptyAllConversations({ commit }) {
    commit(types.EMPTY_ALL_CONVERSATION);
  },

  clearSelectedState({ commit }) {
    commit(types.CLEAR_CURRENT_CHAT_WINDOW);
  },

  fetchPreviousMessages: async ({ commit }, data) => {
    try {
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages(data);
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: data.conversationId,
        data: meta,
      });
      commit(types.SET_PREVIOUS_CONVERSATIONS, {
        id: data.conversationId,
        data: payload,
      });
      if (!payload.length) {
        commit(types.SET_ALL_MESSAGES_LOADED, data.conversationId);
      }
    } catch (error) {
      // Handle error
    }
  },

  fetchMessagesAround: async ({ commit }, { conversationId, around }) => {
    try {
      const {
        data: { meta, payload },
      } = await MessageApi.getMessagesAround({
        conversationId,
        around,
      });
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: conversationId,
        data: meta,
      });
      commit(types.SET_MISSING_MESSAGES, {
        id: conversationId,
        data: payload,
      });
      return payload;
    } catch (error) {
      return [];
    }
  },

  fetchLatestMessages: async ({ commit }, { conversationId }) => {
    try {
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages({
        conversationId,
        before: 2_147_483_648,
      });
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: conversationId,
        data: meta,
      });
      commit(types.SET_MISSING_MESSAGES, {
        id: conversationId,
        data: payload,
      });
      commit(types.CLEAR_ALL_MESSAGES_LOADED, conversationId);
    } catch (error) {
      // Ignore error
    }
  },

  fetchAllAttachments: async ({ commit }, conversationId) => {
    let attachments = [];

    try {
      const { data } = await ConversationApi.getAllAttachments(conversationId);
      attachments = data.payload;
    } catch (error) {
      // in case of error, log the error and continue
      Sentry.setContext('Conversation', {
        id: conversationId,
      });
      Sentry.captureException(error);
    } finally {
      // we run the commit even if the request fails
      // this ensures that the `attachment` variable is always present on chat
      commit(types.SET_ALL_ATTACHMENTS, {
        id: conversationId,
        data: attachments,
      });
    }
  },

  syncActiveConversationMessages: async (
    { commit, state, dispatch },
    { conversationId }
  ) => {
    const { allConversations, syncConversationsMessages } = state;
    const lastMessageId = syncConversationsMessages[conversationId];
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    try {
      const { messages } = selectedChat;
      // Fetch all the messages after the last message id
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages({
        conversationId,
        after: lastMessageId,
      });
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: conversationId,
        data: meta,
      });
      // Find the messages that are not already present in the store
      const missingMessages = payload.filter(
        message => !messages.find(item => item.id === message.id)
      );
      selectedChat.messages.push(...missingMessages);
      // Sort the messages by created_at
      const sortedMessages = selectedChat.messages.sort((a, b) => {
        return new Date(a.created_at) - new Date(b.created_at);
      });
      commit(types.SET_MISSING_MESSAGES, {
        id: conversationId,
        data: sortedMessages,
      });
      commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
        conversationId,
        messageId: null,
      });
      dispatch('markMessagesRead', { id: conversationId }, { root: true });
    } catch (error) {
      // Handle error
    }
  },

  setConversationLastMessageId: async (
    { commit, state },
    { conversationId }
  ) => {
    const { allConversations } = state;
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    const { messages } = selectedChat;
    const lastMessage = messages.last();
    if (!lastMessage) return;
    commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
      conversationId,
      messageId: lastMessage.id,
    });
  },

  async setActiveChat({ commit, dispatch }, { data, after }) {
    commit(types.SET_CURRENT_CHAT_WINDOW, data);
    commit(types.CLEAR_ALL_MESSAGES_LOADED, data.id);
    if (data.dataFetched === undefined) {
      try {
        await dispatch('fetchPreviousMessages', {
          after,
          before: data.messages?.[0]?.id,
          conversationId: data.id,
        });
        commit(types.SET_CHAT_DATA_FETCHED, data.id);
      } catch (error) {
        // Ignore error
      }
    }
  },

  assignAgent: async (
    { dispatch },
    { conversationId, agentId, assigneeType }
  ) => {
    try {
      const response = await ConversationApi.assignAgent({
        conversationId,
        agentId,
        assigneeType,
      });
      dispatch('setCurrentChatAssignee', {
        conversationId,
        assignee: response.data,
        assigneeType,
      });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatAssignee(
    { commit },
    { conversationId, assignee, assigneeType }
  ) {
    commit(types.ASSIGN_AGENT, { conversationId, assignee, assigneeType });
  },

  assignTeam: async ({ dispatch }, { conversationId, teamId }) => {
    try {
      const response = await ConversationApi.assignTeam({
        conversationId,
        teamId,
      });
      dispatch('setCurrentChatTeam', { team: response.data, conversationId });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatTeam({ commit }, { team, conversationId }) {
    commit(types.ASSIGN_TEAM, { team, conversationId });
  },

  toggleStatus: async (
    { commit },
    { conversationId, status, snoozedUntil = null, customAttributes = null }
  ) => {
    try {
      // Update custom attributes first if provided
      if (customAttributes) {
        await ConversationApi.updateCustomAttributes({
          conversationId,
          customAttributes,
        });
        commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, {
          conversationId,
          customAttributes,
        });
      }

      const {
        data: {
          payload: {
            current_status: updatedStatus,
            snoozed_until: updatedSnoozedUntil,
          } = {},
        } = {},
      } = await ConversationApi.toggleStatus({
        conversationId,
        status,
        snoozedUntil,
      });
      commit(types.CHANGE_CONVERSATION_STATUS, {
        conversationId,
        status: updatedStatus,
        snoozedUntil: updatedSnoozedUntil,
      });
    } catch (error) {
      // Handle error
    }
  },

  createPendingMessageAndSend: async ({ dispatch }, data) => {
    const pendingMessage = createPendingMessage(data);
    dispatch('sendMessageWithData', pendingMessage);
  },

  sendMessageWithData: async ({ commit }, pendingMessage) => {
    const { conversation_id: conversationId, id } = pendingMessage;
    try {
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        status: MESSAGE_STATUS.PROGRESS,
      });
      const response = hasMessageFailedWithExternalError(pendingMessage)
        ? await MessageApi.retry(conversationId, id)
        : await MessageApi.create(pendingMessage);
      commit(types.ADD_MESSAGE, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
    } catch (error) {
      const errorMessage = error.response
        ? error.response.data.error
        : undefined;
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        meta: {
          error: errorMessage,
        },
        status: MESSAGE_STATUS.FAILED,
      });
      throw error;
    }
  },

  addMessage({ commit, rootGetters }, message) {
    commit(types.ADD_MESSAGE, message);
    if (message.message_type === MESSAGE_TYPE.INCOMING) {
      commit(types.SET_CONVERSATION_CAN_REPLY, {
        conversationId: message.conversation_id,
        canReply: true,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, message);
    }
    handleVoiceCallCreated(
      message,
      rootGetters?.getCurrentUserID,
      rootGetters?.getCurrentUserAvailability
    );
  },

  updateMessage({ commit, rootGetters }, message) {
    commit(types.ADD_MESSAGE, message);
    handleVoiceCallUpdated(
      commit,
      message,
      rootGetters?.getCurrentUserID,
      rootGetters?.getCurrentUserAvailability
    );
  },

  deleteMessage: async function deleteLabels(
    { commit },
    { conversationId, messageId }
  ) {
    try {
      const { data } = await MessageApi.delete(conversationId, messageId);
      commit(types.ADD_MESSAGE, data);
      commit(types.DELETE_CONVERSATION_ATTACHMENTS, data);
    } catch (error) {
      throw new Error(error);
    }
  },

  editMessage: async function editMessage(
    { commit },
    { conversationId, messageId, content }
  ) {
    try {
      const { data } = await MessageApi.editContent(
        conversationId,
        messageId,
        content
      );
      commit(types.ADD_MESSAGE, data);
    } catch (error) {
      throw new Error(error);
    }
  },

  forwardMessage: async function forwardMessage(
    { dispatch, getters },
    { messageId, targetConversationId }
  ) {
    try {
      const sourceConversation = getters.getSelectedChat;
      const originalMessage = sourceConversation.messages?.find(
        msg => msg.id === messageId
      );

      if (!originalMessage) {
        throw new Error('Message not found');
      }

      let forwardedContent = '';

      // Manejar contenido de texto
      if (originalMessage.content) {
        forwardedContent = `📩 ${originalMessage.content}`;
      }

      // Manejar archivos adjuntos (multimedia)
      // Necesitamos descargar y reenviar los archivos
      if (
        originalMessage.attachments &&
        originalMessage.attachments.length > 0
      ) {
        const files = await Promise.all(
          originalMessage.attachments.map(async attachment => {
            try {
              const response = await fetch(attachment.data_url);
              const blob = await response.blob();

              // Obtener el Content-Type del response HTTP (más confiable)
              const contentType = response.headers.get('content-type');

              // Prioridad: Content-Type del response > blob.type > inferir de extensión
              let mimeType = contentType || blob.type;

              // Si aún no hay tipo válido, inferir de la extensión del archivo
              if (!mimeType || mimeType === 'application/octet-stream') {
                const ext = attachment.extension?.toLowerCase();
                const mimeMap = {
                  jpg: 'image/jpeg',
                  jpeg: 'image/jpeg',
                  png: 'image/png',
                  gif: 'image/gif',
                  webp: 'image/webp',
                  mp4: 'video/mp4',
                  mov: 'video/quicktime',
                  avi: 'video/x-msvideo',
                  mp3: 'audio/mpeg',
                  wav: 'audio/wav',
                  ogg: 'audio/ogg',
                  pdf: 'application/pdf',
                };
                mimeType =
                  mimeMap[ext] || blob.type || 'application/octet-stream';
              }

              // Construir nombre de archivo válido con extensión
              let fileName = attachment.file_name;
              if (!fileName) {
                // Inferir extensión del MIME type si no existe
                const ext =
                  attachment.extension || mimeType.split('/')[1] || 'bin';
                fileName = `file.${ext}`;
              }

              // Debug: verificar qué tipo MIME estamos usando
              if (window.console && window.console.log) {
                window.console.log('📎 Forwarding attachment:', {
                  fileName,
                  extension: attachment.extension,
                  responseContentType: contentType,
                  blobType: blob.type,
                  finalMimeType: mimeType,
                });
              }

              return new File([blob], fileName, {
                type: mimeType,
              });
            } catch (error) {
              // Error downloading attachment, skip this file
              return null;
            }
          })
        );

        const validFiles = files.filter(file => file !== null);

        const pendingMessage = createPendingMessage({
          conversationId: targetConversationId,
          message: forwardedContent,
          private: false,
          files: validFiles,
          content_attributes: {
            forwarded_from_message_id: messageId,
            forwarded_from_conversation_id: originalMessage.conversation_id,
          },
        });

        await dispatch('sendMessageWithData', pendingMessage);
      } else {
        // Solo texto, sin archivos
        const pendingMessage = createPendingMessage({
          conversationId: targetConversationId,
          message: forwardedContent,
          private: false,
          content_attributes: {
            forwarded_from_message_id: messageId,
            forwarded_from_conversation_id: originalMessage.conversation_id,
          },
        });

        await dispatch('sendMessageWithData', pendingMessage);
      }
    } catch (error) {
      throw new Error(error);
    }
  },

  forwardMultipleMessages: async function forwardMultipleMessages(
    { dispatch, getters },
    { messageIds, targetConversationId }
  ) {
    try {
      const sourceConversation = getters.getSelectedChat;
      const messages = sourceConversation.messages?.filter(msg =>
        messageIds.includes(msg.id)
      );

      if (!messages || messages.length === 0) {
        throw new Error('Messages not found');
      }

      // Ordenar mensajes por fecha de creación
      const sortedMessages = messages.sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      );

      // Reenviar cada mensaje en orden
      // eslint-disable-next-line no-restricted-syntax
      for (const message of sortedMessages) {
        let forwardedContent = '';

        // Manejar contenido de texto
        if (message.content) {
          forwardedContent = `📩 ${message.content}`;
        }

        // Manejar archivos adjuntos (multimedia)
        // Necesitamos descargar y reenviar los archivos
        if (message.attachments && message.attachments.length > 0) {
          // eslint-disable-next-line no-await-in-loop
          const files = await Promise.all(
            message.attachments.map(async attachment => {
              try {
                const response = await fetch(attachment.data_url);
                const blob = await response.blob();

                // Obtener el Content-Type del response HTTP (más confiable)
                const contentType = response.headers.get('content-type');

                // Prioridad: Content-Type del response > blob.type > inferir de extensión
                let mimeType = contentType || blob.type;

                // Si aún no hay tipo válido, inferir de la extensión del archivo
                if (!mimeType || mimeType === 'application/octet-stream') {
                  const ext = attachment.extension?.toLowerCase();
                  const mimeMap = {
                    jpg: 'image/jpeg',
                    jpeg: 'image/jpeg',
                    png: 'image/png',
                    gif: 'image/gif',
                    webp: 'image/webp',
                    mp4: 'video/mp4',
                    mov: 'video/quicktime',
                    avi: 'video/x-msvideo',
                    mp3: 'audio/mpeg',
                    wav: 'audio/wav',
                    ogg: 'audio/ogg',
                    pdf: 'application/pdf',
                  };
                  mimeType =
                    mimeMap[ext] || blob.type || 'application/octet-stream';
                }

                // Construir nombre de archivo válido con extensión
                let fileName = attachment.file_name;
                if (!fileName) {
                  // Inferir extensión del MIME type si no existe
                  const ext =
                    attachment.extension || mimeType.split('/')[1] || 'bin';
                  fileName = `file.${ext}`;
                }

                return new File([blob], fileName, {
                  type: mimeType,
                });
              } catch (error) {
                // Error downloading attachment, skip this file
                return null;
              }
            })
          );

          const validFiles = files.filter(file => file !== null);

          const pendingMessage = createPendingMessage({
            conversationId: targetConversationId,
            message: forwardedContent,
            private: false,
            files: validFiles,
            content_attributes: {
              forwarded_from_message_id: message.id,
              forwarded_from_conversation_id: message.conversation_id,
            },
          });

          // eslint-disable-next-line no-await-in-loop
          await dispatch('sendMessageWithData', pendingMessage);
        } else {
          // Solo texto, sin archivos
          const pendingMessage = createPendingMessage({
            conversationId: targetConversationId,
            message: forwardedContent,
            private: false,
            content_attributes: {
              forwarded_from_message_id: message.id,
              forwarded_from_conversation_id: message.conversation_id,
            },
          });

          // eslint-disable-next-line no-await-in-loop
          await dispatch('sendMessageWithData', pendingMessage);
        }

        // Pequeña pausa entre mensajes para mantener el orden
        // eslint-disable-next-line no-await-in-loop
        await new Promise(resolve => {
          setTimeout(resolve, 100);
        });
      }
    } catch (error) {
      throw new Error(error);
    }
  },

  deleteConversation: async ({ commit, dispatch }, conversationId) => {
    try {
      await ConversationApi.delete(conversationId);
      commit(types.DELETE_CONVERSATION, conversationId);
      dispatch('conversationStats/get', {}, { root: true });
    } catch (error) {
      throw new Error(error);
    }
  },

  addConversation({ commit, state, dispatch, rootState }, conversation) {
    const { currentInbox, appliedFilters } = state;
    const {
      inbox_id: inboxId,
      meta: { sender },
    } = conversation;
    const hasAppliedFilters = !!appliedFilters.length;
    const isMatchingInboxFilter =
      !currentInbox || Number(currentInbox) === inboxId;
    if (
      !hasAppliedFilters &&
      !isOnFoldersView(rootState) &&
      !isOnMentionsView(rootState) &&
      !isOnParticipatingView(rootState) &&
      !isOnUnattendedView(rootState) &&
      isMatchingInboxFilter
    ) {
      commit(types.ADD_CONVERSATION, conversation);
      dispatch('contacts/setContact', sender);
    }
  },

  addMentions({ dispatch, rootState }, conversation) {
    if (isOnMentionsView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  addUnattended({ dispatch, rootState }, conversation) {
    if (isOnUnattendedView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  updateConversation({ commit, dispatch, rootGetters }, conversation) {
    const sender = conversation.meta?.sender;

    commit(types.UPDATE_CONVERSATION, conversation);
    syncConversationCallVisibility(conversation, rootGetters?.getCurrentUserID);

    dispatch('conversationLabels/setConversationLabel', {
      id: conversation.id,
      data: conversation.labels,
    });

    if (sender) dispatch('contacts/setContact', sender);
  },

  updateConversationLastActivity(
    { commit },
    { conversationId, lastActivityAt }
  ) {
    commit(types.UPDATE_CONVERSATION_LAST_ACTIVITY, {
      lastActivityAt,
      conversationId,
    });
  },

  setChatStatusFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_STATUS_FILTER, data);
  },

  setChatSortFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_SORT_FILTER, data);
  },

  setChatUnreadFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_UNREAD_FILTER, data);
  },

  async toggleFavorite({ commit, state }, conversationId) {
    const conversation = state.allConversations.find(
      c => c.id === conversationId
    );
    if (!conversation) return;

    const previousState = conversation.is_favorite;
    const newState = !previousState;

    commit(types.TOGGLE_CONVERSATION_FAVORITE, {
      conversationId,
      isFavorite: newState,
    });

    try {
      if (newState) {
        await ConversationApi.toggleFavorite(conversationId);
      } else {
        await ConversationApi.removeFavorite(conversationId);
      }
    } catch (error) {
      commit(types.TOGGLE_CONVERSATION_FAVORITE, {
        conversationId,
        isFavorite: previousState,
      });
      throw error;
    }
  },

  updateAssignee({ commit }, data) {
    commit(types.UPDATE_ASSIGNEE, data);
  },

  updateConversationContact({ commit }, data) {
    if (data.id) {
      commit(`contacts/${types.SET_CONTACT_ITEM}`, data);
    }
    commit(types.UPDATE_CONVERSATION_CONTACT, data);
  },

  setActiveInbox({ commit }, inboxId) {
    commit(types.SET_ACTIVE_INBOX, inboxId);
  },

  muteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.mute(conversationId);
      commit(types.MUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  unmuteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.unmute(conversationId);
      commit(types.UNMUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  sendEmailTranscript: async (_, { conversationId, email }) => {
    await ConversationApi.sendEmailTranscript({ conversationId, email });
  },

  updateCustomAttributes: async (
    { commit },
    { conversationId, customAttributes }
  ) => {
    try {
      const response = await ConversationApi.updateCustomAttributes({
        conversationId,
        customAttributes,
      });
      const { custom_attributes } = response.data;
      commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, {
        conversationId,
        customAttributes: custom_attributes,
      });
    } catch (error) {
      throw new Error(error);
    }
  },

  setConversationFilters({ commit }, data) {
    commit(types.SET_CONVERSATION_FILTERS, data);
  },

  clearConversationFilters({ commit }) {
    commit(types.CLEAR_CONVERSATION_FILTERS);
  },

  setChatListFilters({ commit }, data) {
    commit(types.SET_CHAT_LIST_FILTERS, data);
  },

  updateChatListFilters({ commit }, data) {
    commit(types.UPDATE_CHAT_LIST_FILTERS, data);
  },

  assignPriority: async ({ dispatch }, { conversationId, priority }) => {
    try {
      await ConversationApi.togglePriority({
        conversationId,
        priority,
      });

      dispatch('setCurrentChatPriority', {
        priority,
        conversationId,
      });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatPriority({ commit }, { priority, conversationId }) {
    commit(types.ASSIGN_PRIORITY, { priority, conversationId });
  },

  setContextMenuChatId({ commit }, chatId) {
    commit(types.SET_CONTEXT_MENU_CHAT_ID, chatId);
  },

  getInboxCaptainAssistantById: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.getInboxAssistant(conversationId);
      commit(types.SET_INBOX_CAPTAIN_ASSISTANT, response.data);
    } catch (error) {
      // Handle error
    }
  },

  ...messageReadActions,
  ...messageTranslateActions,
};

export default actions;
